import { createSelector } from "@reduxjs/toolkit";
import { IPixPath, IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import { convertMcBimSpaceToPixPolygon } from "../../DesignTab/pixpolygonSelectors";
import { filterOutContainerMcBimSpaces } from "../../DesignTab/containerSpaceFilter";
import {
  selectDebugMcbimItems,
  selectDebugActiveBuildingStoryName,
  selectDebugHiddenTypeClasses,
  selectDebugItemsToShow,
} from "../DebugSlice";
import { IRootState } from "../../app/store";
import { convertMcBimItemToPixPolygons } from "../../DesignTab/transformMCBimItemsToPolygons";
import { buildCeilingGridPixPathsFromLines } from "../../buildCeilingGridPixPathsFromLines";

export const selectDebugCount = createSelector(selectDebugMcbimItems, (items) => items.length);

export const selectDebugUniqueBuildingStoryNames = createSelector(
  selectDebugMcbimItems,
  (items): string[] => {
    const names = new Set<string>();
    for (const it of items) {
      names.add(getBuildingStoryName(it));
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, "nb"));
  }
);

const getBuildingStoryName = (item: unknown): string => {
  const name = (item as any)?.buildingStory?.name;
  return name && String(name).trim() !== "" ? String(name) : "(uten etasje)";
};

// Items with geometry on active storey
const selectFreeTextSearch = (s: IRootState) => s.debug.freeTextSearch ?? "";
export const selectDebugMcItemsWithGeomOnActiveBuildingStory = createSelector(
  selectDebugMcbimItems,
  selectDebugActiveBuildingStoryName,
  selectFreeTextSearch,
  (items, activeName, freeTextSearch) => {
    return items.filter((it) => {
      if (it.polygon == null && it.bbox == null) return false;
      if (freeTextSearch !== "") {
        const search = freeTextSearch.toUpperCase();
        const inName = it.name && String(it.name).toUpperCase().includes(search);
        const inLongName = it.longName && String(it.longName).toUpperCase().includes(search);
        if (!inName && !inLongName) {
          return false;
        }
      }
      return activeName == null || getBuildingStoryName(it) === activeName;
    });
  }
);

// Spaces â†’ Pix polygons (Spaces pÃ¥verkas inte av hide-filtret)
export const selectDebugSpacePixPolygonsFromMcbimItems = createSelector(
  selectDebugMcItemsWithGeomOnActiveBuildingStory,
  (items): IPixPolygon[] => {
    const spaces = filterOutContainerMcBimSpaces(
      items.filter((it) => it.instanceClass === "IfcSpace")
    );

    return spaces
      .map((it) =>
        convertMcBimSpaceToPixPolygon({
          mcBimSpace: it,
          dragSelectSpaces: false,
        })
      )
      .filter(
        (it) =>
          it !== undefined &&
          JSON.stringify(it.text.values).toUpperCase().includes("AREA") === false
      ) as IPixPolygon[];
  }
);

// Non-spaces â†’ Pix polygons (respekterar hide-filtret)
export const selectDebugNonSpacesPixPolygonsFromMcbimItems = createSelector(
  selectDebugMcItemsWithGeomOnActiveBuildingStory,
  selectDebugHiddenTypeClasses,
  (items, hiddenTypeClasses): IPixPolygon[] => {
    const base = items.filter((it) => it.instanceClass !== "IfcSpace");
    const hasHidden = Array.isArray(hiddenTypeClasses) && hiddenTypeClasses.length > 0;
    const hidden = new Set((hiddenTypeClasses ?? []).map((s) => s.trim()).filter(Boolean));

    const filtered = hasHidden
      ? base.filter((it) => {
          const tc = (it as any)?.type?.typeClass;
          if (!tc) return true; // saknar typeClass -> dÃ¶ljs inte
          return !hidden.has(String(tc).trim());
        })
      : base;

    return filtered
      .map((it) =>
        convertMcBimItemToPixPolygons({
          mcBimItem: it,
          mcbimItemColorMode: "MULTIPLACER_COLORS",
        })
      )
      .filter((p): p is IPixPolygon => p !== undefined);
  }
);

// All (spaces + non-spaces) â†’ Pix polygons
export const selectAllDebugPixPolygonsFromMcbimItems = createSelector(
  selectDebugSpacePixPolygonsFromMcbimItems,
  selectDebugNonSpacesPixPolygonsFromMcbimItems,
  (spaces, nonSpaces) => [...spaces, ...nonSpaces]
);

// FINAL: filter-aware polygons to render based on itemsToShow
export const selectDebugPixPolygonsFiltered = createSelector(
  selectDebugItemsToShow,
  selectDebugSpacePixPolygonsFromMcbimItems,
  selectDebugNonSpacesPixPolygonsFromMcbimItems,
  selectAllDebugPixPolygonsFromMcbimItems,
  (itemsToShow, spaces, nonSpaces, all) => {
    switch (itemsToShow) {
      case "spaces":
        return spaces;
      case "non-spaces":
        return nonSpaces;
      default:
        return all;
    }
  }
);

// Typen du bad om
export type PixBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

export const selectSpaces = createSelector(
  selectDebugMcItemsWithGeomOnActiveBuildingStory,
  (items) => {
    return items.filter((it) => it.instanceClass === "IfcSpace");
  }
);

export const selectCeilingCoveringsWithCeilingGrids = createSelector(
  selectDebugMcbimItems,
  selectDebugActiveBuildingStoryName,
  (items, activeName) => {
    return items.filter((it) => {
      const predefinedType = String(
        (it as any).predefinedType ?? (it as any).type?.predefinedType ?? ""
      ).toUpperCase();
      const grids = it.ceilingGrids;

      return (
        it.instanceClass === "IfcCovering" &&
        predefinedType === "CEILING" &&
        (activeName == null || getBuildingStoryName(it) === activeName) &&
        grids != null &&
        Array.isArray(grids) &&
        grids.length > 0
      );
    });
  }
);

export const selectDebugCeilingGridPixPathsFromRawLines = createSelector(
  selectCeilingCoveringsWithCeilingGrids,
  (items): IPixPath[] => buildCeilingGridPixPathsFromLines(items)
);

// Selector som returnerar PixBounds | null
export const selectViewerPixBounds = createSelector(
  selectDebugPixPolygonsFiltered,
  selectDebugCeilingGridPixPathsFromRawLines,
  (polys, paths): PixBounds | null => {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    for (const shape of [...polys, ...paths]) {
      if (!shape?.points?.length) continue;
      for (const pt of shape.points) {
        if (!Array.isArray(pt) || pt.length < 2) continue;
        const x = Number(pt[0]);
        const y = Number(pt[1]);
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }

    if (!Number.isFinite(minX)) return null;
    return { minX, minY, maxX, maxY };
  }
);

// Unika type.typeClass-vÃ¤rden fÃ¶r aktiva byggnadsplanet (fÃ¶r UI-listor etc)
export const selectDebugUniqueTypeClassesOnActiveBuildingStory = createSelector(
  selectDebugMcItemsWithGeomOnActiveBuildingStory,
  (items): string[] => {
    const classes = new Set<string>();
    for (const it of items) {
      const tc = (it as any)?.type?.typeClass;
      if (tc && typeof tc === "string" && tc.trim() !== "") {
        classes.add(tc.trim());
      }
    }
    return Array.from(classes).sort((a, b) => a.localeCompare(b, "nb"));
  }
);

export const selectIfcSpacesWithMissingPolygon = createSelector(selectDebugMcbimItems, (items) => {
  return items.filter(
    (it) => it.instanceClass === "IfcSpace" && (it.polygon == null || it.polygon.length === 0)
  );
});
